import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ChatBubbleLeftIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
interface MyStreamWaveTabsDefaultProps {
  readonly wave: ApiWave;
  readonly viewMode: WaveViewMode;
  readonly onToggleViewMode: () => void;
  readonly showGalleryToggle: boolean;
}
const MyStreamWaveTabsDefault: React.FC<MyStreamWaveTabsDefaultProps> = t0 => {
  const $ = _c(47);
  const {
    wave,
    viewMode,
    onToggleViewMode,
    showGalleryToggle
  } = t0;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "S";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  let t1;
  if ($[0] !== isApp || $[1] !== router || $[2] !== searchParams || $[3] !== wave.chat.scope.group?.is_direct_message) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = router;
    $[2] = searchParams;
    $[3] = wave.chat.scope.group?.is_direct_message;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleMobileBack = t1;
  let t2;
  if ($[5] !== pathname || $[6] !== router || $[7] !== searchParams || $[8] !== setActiveContentTab || $[9] !== wave.id || $[10] !== waveChatScroll) {
    t2 = serialNo => {
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[5] = pathname;
    $[6] = router;
    $[7] = searchParams;
    $[8] = setActiveContentTab;
    $[9] = wave.id;
    $[10] = waveChatScroll;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleSearchSelect = t2;
  let t3;
  if ($[12] !== handleMobileBack || $[13] !== isMobile) {
    t3 = isMobile && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[12] = handleMobileBack;
    $[13] = isMobile;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== isMobile || $[16] !== onToggleViewMode || $[17] !== showGalleryToggle || $[18] !== viewMode || $[19] !== wave.contributors_overview || $[20] !== wave.name || $[21] !== wave.picture) {
    t4 = isMobile ? <button type="button" onClick={() => setIsSearchOpen(true)} aria-label="Search messages in this wave" className="tw-flex tw-min-w-0 tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><div className="tw-size-6 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950 lg:tw-size-9"><WavePicture name={wave.name} picture={wave.picture} contributors={wave.contributors_overview.map(_temp)} /></div><h1 className="tw-mb-0 tw-ml-3 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1></button> : <><div className="tw-size-6 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950 lg:tw-size-9"><WavePicture name={wave.name} picture={wave.picture} contributors={wave.contributors_overview.map(_temp2)} /></div><h1 className="tw-mb-0 tw-ml-3 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>{showGalleryToggle && <button type="button" onClick={onToggleViewMode} aria-label={viewMode === "chat" ? "Switch to gallery view" : "Switch to chat view"} className="tw-ml-2 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{viewMode === "chat" ? <Squares2X2Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ChatBubbleLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}</button>}<button type="button" onClick={() => setIsSearchOpen(true)} aria-label="Search messages in this wave" className="tw-ml-2 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white"><MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></button></>;
    $[15] = isMobile;
    $[16] = onToggleViewMode;
    $[17] = showGalleryToggle;
    $[18] = viewMode;
    $[19] = wave.contributors_overview;
    $[20] = wave.name;
    $[21] = wave.picture;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t3 || $[24] !== t4) {
    t5 = <div className="tw-flex tw-min-w-0 tw-items-center">{t3}{t4}</div>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const t6 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t7;
  if ($[26] !== t6) {
    t7 = <ChevronDoubleLeftIcon strokeWidth={2} className={t6} />;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t7 || $[29] !== toggleRightSidebar) {
    t8 = <div className="tw-relative tw-flex tw-items-center tw-self-stretch"><button type="button" onClick={toggleRightSidebar} className="tw-group tw-absolute tw-right-0 tw-top-1/2 tw-flex tw-h-8 tw-w-8 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label="Toggle right sidebar">{t7}</button></div>;
    $[28] = t7;
    $[29] = toggleRightSidebar;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t5 || $[32] !== t8) {
    t9 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-2 tw-py-3 sm:tw-px-4">{t5}{t8}</div>;
    $[31] = t5;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== activeContentTab || $[35] !== setActiveContentTab || $[36] !== wave) {
    t10 = <div className="tw-border-b tw-border-l-0 tw-border-t-0 tw-border-solid tw-border-iron-800"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} /></div>;
    $[34] = activeContentTab;
    $[35] = setActiveContentTab;
    $[36] = wave;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsSearchOpen(false);
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== handleSearchSelect || $[40] !== isSearchOpen || $[41] !== wave) {
    t12 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t11} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[39] = handleSearchSelect;
    $[40] = isSearchOpen;
    $[41] = wave;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t10 || $[44] !== t12 || $[45] !== t9) {
    t13 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t9}{t10}{t12}</div>;
    $[43] = t10;
    $[44] = t12;
    $[45] = t9;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  return t13;
};
export default MyStreamWaveTabsDefault;
function _temp(c) {
  return {
    pfp: c.contributor_pfp
  };
}
function _temp2(c_0) {
  return {
    pfp: c_0.contributor_pfp
  };
}